import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "./App.scss";
import "./components/components.scss";
import "./containers/containers.scss";
import "./containers/pageContents/pageContent.scss";
import "./components/staticComponents/popups.scss";
import "./components/staticComponents/HighlightNotes.scss";
import Banner from "./containers/Banner";
import MainContent from "./containers/MainContent";
import PasswordInput from "./containers/PasswordInput";
import Dashboard from "./containers/Dashboard";
import LockedNotice from "./containers/LockedNotice";
import { watchForLeave } from "./utils/leaveGuard";
import { saveProgress, saveProgressBeacon } from "./utils/progress";
import {
  getGroupHint,
  getGroupTypeMapping,
  parseAnswers,
  parseGroupAnswerContent,
  questionReg,
  questionsReg,
} from "./containers/pageContents/Const";

/** 作答中每隔多久把進度存回伺服器一次 */
const SAVE_EVERY_MS = 20000;

const App = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [urlKey, setUrlKey] = useState("");
  const [candidate, setCandidate] = useState(null);
  const [examContent, setExamContent] = useState(null);
  const [testPages, setTestPages] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [testType, setTestType] = useState("L");
  const [testTime, setTestTime] = useState(0);
  const [pageType, setPageType] = useState("Intro");
  const [pageNumber, setPageNumber] = useState(1);
  const [playTestSound, setPlayTestSound] = useState(false);
  const [testEnd, setTestEnd] = useState(false);
  // 連結種類：dashboard＝一場模考的總連結／section＝單科連結（舊信裡的）
  const [linkKind, setLinkKind] = useState("section");
  // 總連結的密碼記在這裡：考完一科回到科目頁時不用再輸一次。
  // 只存在記憶體，重新整理就沒了（那時本來就該重新驗證）。
  const [dashboardPassword, setDashboardPassword] = useState("");
  // 閱讀／寫作頁的左右欄比例與字級。放在這裡是為了讓考生調過一次之後，
  // 換頁、甚至換科目都維持同一個設定，不必每頁重調。
  const [splitRatio, setSplitRatio] = useState(50);
  const [fontScale, setFontScale] = useState(1);
  // 正式模考中途離開被鎖住時，整個畫面換成說明頁
  const [locked, setLocked] = useState(null);
  /*
   * 作答狀態集中放在這裡（原本分散在 MainContent 與 TestPages）。
   * 續考要能把「當時寫到哪」整組存下來、解鎖回來原樣還原，
   * 狀態散在兩層就湊不齊，也沒有一個地方負責存檔。
   */
  const [storedAnswers, setStoredAnswers] = useState([]);
  const [writingAnswerNote, setWritingAnswerNote] = useState([]);
  const [answeredQuestion, setAnsweredQuestion] = useState([]);
  const [reviewModeNode, setReviewModeNode] = useState([]);
  // 續考時計時器要接著剩下的秒數跑；null 代表照原本的科目時長從頭算
  const [startingSeconds, setStartingSeconds] = useState(null);
  // 聽力音檔續播的位置（秒）
  const [audioSeek, setAudioSeek] = useState(0);

  // 每秒都在變的兩個值放 ref，不放 state——放 state 會讓整頁每秒重畫一次
  const remainingRef = useRef(null);
  const audioTimeRef = useRef(0);
  // 最新一份作答狀態。存檔的計時器只讀這裡，就不必把每個按鍵都寫進相依陣列
  const snapshotRef = useRef(null);

  // console.log(answers);

  useEffect(() => {
    let path = window.location.pathname;

    const url = process.env.REACT_APP_IS_URLKEY_VALID + path;
    axios.get(`${url}`).then((res) => {
      const data = res.data;
      if (data.success) {
        setUrlKey(data.urlKey);
        setLinkKind(data.kind === "dashboard" ? "dashboard" : "section");
      } else {
        if (process.env.NODE_ENV === "development") {
          return;
        } else {
          window.location.href = process.env.REACT_APP_HOMEPAGE;
        }
      }
    });
  }, []);

  /**
   * 正式模考的中途離開偵測。
   *
   * 只在「已進入某一科、還沒交卷、而且是正式模考」時才監看；
   * 自我練習不鎖（伺服器端也會再判斷一次模式）。
   */
  useEffect(() => {
    if (!isLoaded || !candidate || !candidate.examDetailId) {
      return undefined;
    }
    if (candidate.mode === "practice") {
      return undefined;
    }
    if (testEnd || pageType === "End" || locked) {
      return undefined;             // 已交卷，關掉頁面是正常行為
    }

    return watchForLeave({
      examDetailId: candidate.examDetailId,
      onLocked: (info) => setLocked(info),
    });
  }, [isLoaded, candidate, testEnd, pageType, locked]);

  // 每次重畫都把最新狀態記進 ref，供下面的存檔計時器讀取。
  // 沒有相依陣列是刻意的——就是要每次重畫都更新。
  useEffect(() => {
    snapshotRef.current = {
      storedAnswers,
      writingAnswerNote,
      answeredQuestion,
      reviewModeNode,
      pageNumber,
    };
  });

  /**
   * 作答中的自動存檔。
   *
   * 存兩種時機：每 SAVE_EVERY_MS 一次，以及頁面要被藏起來／關掉的當下。
   * 後者用 sendBeacon——那個時間點 XHR 不保證送得出去，而正式模考正是
   * 在那一刻會被鎖住，等於最需要存到的一次。
   *
   * 已交卷或已被鎖住就不再存（伺服器端也會再擋一次）。
   */
  useEffect(() => {
    if (!isLoaded || !candidate || !candidate.examDetailId) {
      return undefined;
    }
    if (pageType !== "Tests" || testEnd || locked) {
      return undefined;
    }

    const detailId = candidate.examDetailId;
    const now = () => ({
      // audioTime 每秒變好幾次但不會觸發重畫，所以存檔當下才讀
      state: { ...snapshotRef.current, audioTime: audioTimeRef.current },
      remaining:
        remainingRef.current === null ? testTime * 60 : remainingRef.current,
    });

    const save = () => {
      const { state, remaining } = now();
      saveProgress(detailId, state, remaining).catch(() => {
        // 存檔失敗不打擾考生——下一輪會再試一次
      });
    };

    // 進場先存一次：萬一才剛開始就被鎖住，也還原得回來
    save();
    const timer = setInterval(save, SAVE_EVERY_MS);

    const flush = () => {
      const { state, remaining } = now();
      saveProgressBeacon(detailId, state, remaining);
    };
    const onVisibility = () => {
      if (document.hidden) {
        flush();
      }
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(timer);
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
      flush();
    };
  }, [isLoaded, candidate, pageType, testEnd, locked, testTime]);

  const mainContent = () => {
    if (isLoaded) {
      return (
        <MainContent
          candidate={candidate}
          testPages={testPages}
          answers={answers}
          testType={testType}
          pageType={pageType}
          pageNumber={pageNumber}
          renderNextPage={renderNextPage}
          renderPreviousPage={renderPreviousPage}
          updatePlayTestSound={updatePlayTestSound}
          testSoundBtn={playTestSound ? "Stop sound" : "Play sound"}
          TestsPageJump={TestsPageJump}
          startReviewAnswers={startReviewAnswers}
          testEnd={testEnd}
          forceEndTest={forceEndTest}
          fromDashboard={linkKind === "dashboard"}
          returnToDashboard={returnToDashboard}
          splitRatio={splitRatio}
          setSplitRatio={setSplitRatio}
          fontScale={fontScale}
          storedAnswers={storedAnswers}
          setStoredAnswers={setStoredAnswers}
          writingAnswerNote={writingAnswerNote}
          setWritingAnswerNote={setWritingAnswerNote}
          answeredQuestion={answeredQuestion}
          setAnsweredQuestion={setAnsweredQuestion}
          reviewModeNode={reviewModeNode}
          setReviewModeNode={setReviewModeNode}
        />
      );
    }
    // 總連結 → dashboard（三科卡片、依序解鎖）
    // 單科連結 → 原本的密碼頁，舊信裡的連結照樣能用
    if (linkKind === "dashboard") {
      return (
        <Dashboard
          handleLoading={handleLoading}
          urlKey={urlKey}
          savedPassword={dashboardPassword}
          onAuthenticated={setDashboardPassword}
        />
      );
    }
    return <PasswordInput handleLoading={handleLoading} urlKey={urlKey} />;
  };

  /**
   * 考完一科回到科目卡片頁。
   *
   * 不用 window.location.reload()——整頁重載會把密碼一起丟掉，學生得再輸一次，
   * 而且 pagehide 會被當成離開頁面。這裡只把「某一科的作答狀態」清乾淨，
   * Dashboard 會帶著記住的密碼重新抓一次科目狀態：剛考完的那科變成已完成、
   * 下一科才會解鎖。
   */
  const returnToDashboard = () => {
    setIsLoaded(false);
    setCandidate(null);
    setExamContent(null);
    setTestPages([]);
    setAnswers([]);
    setTestType("L");
    setTestTime(0);
    setPageType("Intro");
    setPageNumber(1);
    setPlayTestSound(false);
    setTestEnd(false);
    setLocked(null);
    setStoredAnswers([]);
    setWritingAnswerNote([]);
    setAnsweredQuestion([]);
    setReviewModeNode([]);
    setStartingSeconds(null);
    setAudioSeek(0);
    remainingRef.current = null;
    audioTimeRef.current = 0;
  };

  const handleLoading = (candidate, examContent) => {
    console.log(examContent.pages);
    // console.log(examContent.answers);
    examContent.pages.forEach((page, pageIndex) => {
      const formattedGroups = [];
      if (examContent.test_type === "W") {
        formattedGroups.push({
          type: "writing",
          number: pageIndex + 1,
          content: [
            {
              id: pageIndex + 1,
              answers: [],
            },
          ],
        });
      }

      page.groups.forEach((group) => {
        const questionList = [];
        let questionWording = "";

        const groupMatches = group.question_html.matchAll(questionReg);
        for (let match of groupMatches) {
          questionList.push(match[1]);
        }

        const groupMultiMatches = group.question_html.matchAll(questionsReg);
        for (let match of groupMultiMatches) {
          const startend = match[1].split("-");
          for (let i = parseInt(startend[0]); i <= parseInt(startend[1]); i++) {
            questionList.push(`${i}`);
          }
        }

        group.questions.forEach((question) => {
          if (!question.seq) {
            return;
          }

          if (question.seq.replaceAll(" ", "").match(/^\d+-\d+$/)) {
            const startend = question.seq.split("-");
            for (
              let i = parseInt(startend[0].replaceAll(" ", ""));
              i <= parseInt(startend[1].replaceAll(" ", ""));
              i++
            ) {
              questionList.push(`${i}`);
            }
          } else {
            questionList.push(question.seq);
          }
        });

        if (questionList.length === 0) {
          return;
        }

        questionList.sort((a, b) => a - b);
        questionList.forEach((questionSeq, index) => {
          if (index === 0) {
            questionWording += `${questionSeq}`;
            return;
          }

          if (questionList[index] - questionList[index - 1] === 1) {
            return;
          }
          questionWording += ` - ${questionList[index - 1]}, `;
          questionWording += `${questionList[index]}`;
        });
        if (
          questionWording.slice(
            questionList[questionList.length - 1].length * -1
          ) !== questionList[questionList.length - 1]
        ) {
          questionWording += ` - ${questionList[questionList.length - 1]}`;
        }

        // Adapt to original format
        const groupData = {
          question: `Questions ${questionWording}`,
          description: getGroupHint(group.type, group.question_limit),
          number: questionList.length,
          ...getGroupTypeMapping(group),
          ...parseGroupAnswerContent(group),
        };
        formattedGroups.push(groupData);
      });
      page.page = pageIndex + 1;
      page.formattedGroups = formattedGroups;

      // 題目內的拖拉答題
      const contentMatches = page.content.matchAll(questionReg);
      let pageContent = page.content;
      const parsedContent = [];
      for (let questionMatch of contentMatches) {
        const seq = parseInt(questionMatch[1]);
        const splitContent = pageContent.split(questionMatch[0]);
        parsedContent.push({
          id: seq,
          question: splitContent[0],
        });

        pageContent = splitContent.slice(1).join("");
      }
      if (parsedContent.length === 0) {
        return;
      }
      page.questionType = "drop in answer part";
      page.parsedContent = parsedContent;
      page.postContent = pageContent;
    });

    setCandidate(candidate);
    setExamContent(examContent);
    setTestPages(examContent.pages);
    setAnswers(parseAnswers(examContent.pages, examContent.answers));

    let testType = "L";
    let testTime = 30;
    switch (examContent.test_type) {
      case "L":
        break;
      case "R":
        testType = "R";
        testTime = 60;
        break;
      case "W":
        testType = "W";
        testTime = 60;
        break;
      default:
        throw new Error();
    }
    setTestType(testType);
    setTestTime(testTime);
    remainingRef.current = testTime * 60;

    /*
     * 續考：上次存到一半的答案與剩餘時間。
     *
     * 有進度就直接回到當時那一頁，不再走一次確認資料／說明頁——那些在
     * 第一次進場時就做過了，重看一遍只是浪費被鎖走的時間。計時器接著
     * 剩下的秒數跑，聽力音檔也從當時播到的位置接下去。
     */
    const progress = examContent.progress;
    const state = progress && progress.state ? progress.state : null;
    let resumed = false;
    if (state) {
      setStoredAnswers(state.storedAnswers || []);
      setWritingAnswerNote(state.writingAnswerNote || []);
      setAnsweredQuestion(state.answeredQuestion || []);
      setReviewModeNode(state.reviewModeNode || []);
      setStartingSeconds(progress.remaining);
      remainingRef.current = progress.remaining;
      setAudioSeek(state.audioTime || 0);
      audioTimeRef.current = state.audioTime || 0;

      const savedPage = Number(state.pageNumber);
      if (savedPage >= 4 && savedPage <= examContent.pages.length + 3) {
        setPageNumber(savedPage);
        setPageType("Tests");
        resumed = true;
      }
    }

    /*
     * 自我練習：跳過確認資料頁與考試說明頁，直接進題目。
     *
     * 「Confirm your details」是給有監考人員的正式模考核對身分用的，
     * 自我練習沒有監考人員，而且學習系統沒有生日欄位，那一頁只會顯示
     * 哨兵值 1900-01-01，看起來像資料錯誤。說明頁同理——同一個學生
     * 會重複練很多份考卷，每次都再讀一次是純粹的阻力。
     *
     * 聽力仍保留試音頁：沒聲音要在計時開始前發現，而且瀏覽器的自動播放
     * 限制也需要那一下點擊當作使用者操作，否則音檔可能放不出來。
     */
    if (candidate && candidate.mode === "practice" && !resumed) {
      if (testType === "L") {
        setPageNumber(2);
      } else {
        setPageNumber(4);
        setPageType("Tests");
      }
    }

    setIsLoaded(true);
  };

  const renderNextPage = () => {
    // 自我練習的聽力：試音完直接進題目，不再走一次說明頁
    if (candidate && candidate.mode === "practice" && pageNumber === 2) {
      setPageNumber(4);
      setPageType("Tests");
      return;
    }

    if (pageNumber === 1 && testType !== "L") {
      //skip TestSound component if not listening test
      setPageNumber(pageNumber + 2);
    } else {
      setPageNumber(pageNumber + 1);
    }

    if (pageNumber === 3) {
      setPageType("Tests");
    } else if (pageNumber === testPages.length + 3) {
      setPageType("End");
    }
  };

  const renderPreviousPage = () => {
    if (pageNumber === 4 && pageType === "Tests") {
      return;
    }
    setPageNumber(pageNumber - 1);
  };

  const TestsPageJump = (page) => {
    if (page) {
      setPageNumber(page);
    }
  };

  const forceEndTest = () => {
    setPageNumber(4);
    setPageType("End");
    setTestEnd(true);
  };

  const startReviewAnswers = () => {
    setPageNumber(4);
    setPageType("Tests");
  };

  const updatePlayTestSound = () => {
    setPlayTestSound(!playTestSound);
  };

  if (locked) {
    return <LockedNotice reason={locked.reason} />;
  }

  // 作答中整頁鎖成一個視窗高：內容區自己捲，題號列永遠看得到
  const inTest = isLoaded && pageType === "Tests" && !locked;

  return (
    <div className={`App${inTest ? " exam-fullscreen" : ""}`}>
      <Banner
        candidate={candidate}
        testType={testType}
        testTime={testTime}
        pageType={pageType}
        pageNumber={pageNumber}
        audioPath={examContent?.audio ?? ""}
        forceEndTest={forceEndTest}
        playTestSound={playTestSound}
        fontScale={fontScale}
        setFontScale={setFontScale}
        startingSeconds={startingSeconds}
        onTick={(secondsLeft) => {
          remainingRef.current = secondsLeft;
        }}
        audioSeek={audioSeek}
        onAudioTime={(t) => {
          audioTimeRef.current = t;
        }}
      />
      {mainContent()}
    </div>
  );
};

export default App;
